import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Package, TrendingUp, AlertTriangle,
  Loader2, Percent, ArrowRight, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAAS_CATEGORIES = new Set(['Microsoft 365', 'Google Workspace', 'Email Hosting']);

const CATEGORY_STYLES = {
  'Microsoft 365':      { bg: 'bg-blue-50 text-blue-700 border-blue-200',    icon: '🪟' },
  'Google Workspace':   { bg: 'bg-red-50 text-red-700 border-red-200',       icon: '🔤' },
  'Email Hosting':      { bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: '📧' },
};

const CYCLE_LABELS = {
  monthly:    'Monthly',
  quarterly:  'Quarterly',
  half_yearly:'Half Yearly',
  yearly:     'Yearly',
};

const UNIT_LABEL = { per_user: '/user', per_account: '/user', per_service: '' };

export default function ResellerCatalog() {
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [modal, setModal]         = useState(null);   // { product, pricing }
  const [prices, setPrices]       = useState({});     // { cycle → selling_price }
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reseller/pricing`);
      // API returns {product: {...}, custom_pricing: {...}, effective_base_prices: {...}} — flatten
      const flat = res.data.map(item => ({
        ...(item.product || item),
        custom_pricing: item.custom_pricing,
        effective_base_prices: item.effective_base_prices,
      }));
      const saas = flat.filter(p => SAAS_CATEGORIES.has(p.category));
      setProducts(saas);
    } catch {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSetPrice = (p) => {
    const cp = p.custom_pricing?.custom_prices?.add || {};
    const wholesale = p.effective_base_prices || {};
    const init = {};
    (p.enabled_billing_cycles || Object.keys(wholesale)).forEach(cycle => {
      init[cycle] = cp[cycle] || '';
    });
    setPrices(init);
    setModal(p);
  };

  const handleSave = async () => {
    if (!modal) return;
    const customAdd = {};
    let hasPrice = false;
    Object.entries(prices).forEach(([cycle, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) {
        customAdd[cycle] = num;
        hasPrice = true;
      }
    });
    if (!hasPrice) { toast.error('Enter at least one selling price'); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/reseller/pricing`, {
        product_id: modal.id,
        custom_prices: { add: customAdd, renew: customAdd },
        is_visible: true,
      });
      toast.success('Prices saved successfully');
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const getMargin = (sell, buy) => {
    if (!sell || !buy || buy === 0) return null;
    const pct = ((sell - buy) / buy) * 100;
    const amt = sell - buy;
    return { pct: pct.toFixed(1), amt: Math.round(amt), positive: sell >= buy };
  };

  const getSellPrice = (p, cycle) =>
    p.custom_pricing?.custom_prices?.add?.[cycle] || null;

  const filtered = catFilter === 'all' ? products : products.filter(p => p.category === catFilter);

  const belowCostCount = products.filter(p =>
    (p.custom_pricing?.below_cost_cycles || []).length > 0
  ).length;

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Product Catalog</h1>
            <p className="text-sm text-slate-500 mt-0.5">Set your selling prices for SaaS products</p>
          </div>
          {belowCostCount > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm font-medium" data-testid="below-cost-banner">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {belowCostCount} product{belowCostCount > 1 ? 's have' : ' has'} selling price below wholesale cost
            </div>
          )}
        </div>

        {/* Category filter */}
        <div className="flex gap-2 flex-wrap">
          {['all', ...SAAS_CATEGORIES].map(cat => (
            <button
              key={cat}
              onClick={() => setCatFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                catFilter === cat
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat === 'all' ? `All (${products.length})` : cat}
            </button>
          ))}
        </div>

        {/* Products */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-slate-400">
            <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No catalog products found</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map(p => {
              const meta = CATEGORY_STYLES[p.category] || { bg: 'bg-slate-100 text-slate-700', icon: '📦' };
              const unit = UNIT_LABEL[p.pricing_unit] || '';
              const cycles = p.enabled_billing_cycles || ['monthly'];
              const firstCycle = cycles[0];
              const wholesale = p.effective_base_prices?.[firstCycle] || 0;
              const sell = getSellPrice(p, firstCycle);
              const margin = sell ? getMargin(sell, wholesale) : null;
              const belowCost = (p.custom_pricing?.below_cost_cycles || []).length > 0;
              const hasPrices = cycles.some(c => getSellPrice(p, c));

              return (
                <Card
                  key={p.id}
                  className={`relative overflow-hidden border transition-shadow hover:shadow-md ${belowCost ? 'border-red-300' : 'border-slate-200'}`}
                  data-testid={`catalog-card-${p.id}`}
                >
                  {belowCost && (
                    <div className="absolute top-0 inset-x-0 bg-red-500 text-white text-xs text-center py-0.5 font-medium">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Selling price below wholesale cost
                    </div>
                  )}
                  <CardHeader className={`pb-2 ${belowCost ? 'pt-7' : 'pt-4'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className={`text-xs ${meta.bg}`}>{meta.icon} {p.category}</Badge>
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            <Users className="h-3 w-3 mr-1" />
                            Per User
                          </Badge>
                        </div>
                        <CardTitle className="text-base leading-snug text-slate-800">{p.name}</CardTitle>
                      </div>
                      {hasPrices && (
                        <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-1" />
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3 pb-4">
                    {/* Pricing rows for each cycle */}
                    <div className="space-y-1.5">
                      {cycles.map(cycle => {
                        const w = p.effective_base_prices?.[cycle] || 0;
                        const s = getSellPrice(p, cycle);
                        const m = s ? getMargin(s, w) : null;
                        return (
                          <div key={cycle} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2.5 py-1.5">
                            <span className="text-slate-500 text-xs">{CYCLE_LABELS[cycle] || cycle}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-xs">Cost: ₹{w.toLocaleString()}{unit}</span>
                              <ArrowRight className="h-3 w-3 text-slate-300" />
                              {s ? (
                                <span className={`font-semibold text-xs ${m?.positive ? 'text-green-700' : 'text-red-600'}`}>
                                  ₹{s.toLocaleString()}{unit}
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs italic">not set</span>
                              )}
                              {m && (
                                <span className={`text-xs rounded px-1 ${m.positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {m.positive ? '+' : ''}{m.pct}%
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      className="w-full"
                      variant={hasPrices ? 'outline' : 'default'}
                      size="sm"
                      onClick={() => openSetPrice(p)}
                      data-testid={`set-price-btn-${p.id}`}
                    >
                      <Percent className="h-3.5 w-3.5 mr-1.5" />
                      {hasPrices ? 'Update My Prices' : 'Set My Price'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Set My Price Modal */}
      <Dialog open={!!modal} onOpenChange={(o) => { if (!o) setModal(null); }}>
        <DialogContent className="max-w-md" data-testid="set-price-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Set Selling Prices — {modal?.name}</DialogTitle>
          </DialogHeader>
          {modal && (
            <div className="space-y-4 pt-1">
              <p className="text-xs text-slate-500">
                Enter your selling price per billing cycle. Profit = Selling Price − Wholesale Cost.
              </p>

              <div className="space-y-3">
                {(modal.enabled_billing_cycles || ['monthly']).map(cycle => {
                  const wholesale = modal.effective_base_prices?.[cycle] || 0;
                  const sellVal = prices[cycle];
                  const num = parseFloat(sellVal);
                  const margin = !isNaN(num) && num > 0 && wholesale > 0
                    ? getMargin(num, wholesale) : null;

                  return (
                    <div key={cycle} className="bg-slate-50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">{CYCLE_LABELS[cycle] || cycle}</span>
                        <span className="text-xs text-slate-400">
                          Wholesale: ₹{wholesale.toLocaleString()}{UNIT_LABEL[modal.pricing_unit] || ''}
                        </span>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={prices[cycle] ?? ''}
                          onChange={e => setPrices(p => ({ ...p, [cycle]: e.target.value }))}
                          placeholder={`Your selling price`}
                          className="pl-7"
                          data-testid={`price-input-${cycle}`}
                        />
                      </div>
                      {margin && (
                        <div className={`flex items-center gap-2 text-xs ${margin.positive ? 'text-green-700' : 'text-red-600'}`}>
                          <TrendingUp className="h-3 w-3" />
                          <span>
                            {margin.positive ? 'Profit' : 'Loss'}: ₹{Math.abs(margin.amt).toLocaleString()} ({margin.positive ? '+' : ''}{margin.pct}%)
                          </span>
                          {!margin.positive && (
                            <span className="bg-red-100 border border-red-200 rounded px-1">Below cost!</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setModal(null)}>Cancel</Button>
                <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-prices-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                  Save Prices
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
